/**
 * The packaging panel's vocabulary and display units, kept out of the component.
 *
 * Same contract as `@/lib/water`: **no brewing maths is reimplemented here.**
 * Residual CO₂, the priming dose, the keg pressure fit and every unit
 * conversion come from `@beerwright/core` (`carbonation.ts` and `units/`); this
 * module only picks the display unit, names the bottles, and turns the BJCP
 * category number into the sentence the panel prints. If a number on screen
 * ever disagrees with the engine it is because the engine changed, not because
 * a second copy of a formula drifted.
 */

import type { Style } from '@beerwright/catalog';
import type { RecipeFermentationStep, UnitPreferences } from '@beerwright/commands';
import { toFermentationStep } from '@beerwright/commands';
import type { ResidualCo2 } from '@beerwright/core';
import {
  peakFermentationTempC as corePeakFermentationTempC,
  fluidOuncesToLitres,
  scheduleResidualCo2,
} from '@beerwright/core';
import type { UnitKey } from '@/lib/units';
import {
  canonicalInUnit,
  displayUnit,
  formatInUnit,
  formatQuantity,
  unitKeyLabel,
  unitLabel,
} from '@/lib/units';

// ---------------------------------------------------------------------------
// Pressure
// ---------------------------------------------------------------------------

/** The pressure units a regulator can be marked in — the workspace preference. */
export type PressureUnit = UnitPreferences['pressure'];

/**
 * Canonical pressure is **PSI**.
 *
 * The odd one out among the app's canonical units, and deliberately so: core's
 * carbonation fit is a polynomial in °F and psi (see `pressureFromCo2Volumes`),
 * every published carbonation table is printed in psi, and the American
 * regulators most homebrewers own are marked in it. Converting to bar for
 * storage and back for the table would round-trip through the display unit for
 * no gain. A fermentation step's `pressurePsi` is stored the same way.
 *
 * The conversions and the decimal places live in `lib/units`' registry with
 * every other unit in the app, under the `pressure` kind. This map is only the
 * bridge from the workspace preference's own spelling to the registry's key —
 * the two vocabularies are one rename apart and neither is worth changing.
 */
const PRESSURE_UNIT_KEYS: Readonly<Record<PressureUnit, UnitKey>> = {
  psi: 'psi',
  bar: 'bar',
  kpa: 'kpa',
};

/** The suffix a pressure is written with, e.g. `psi`, `bar`, `kPa`. */
export function pressureLabel(unit: PressureUnit): string {
  return unitKeyLabel(PRESSURE_UNIT_KEYS[unit]);
}

/**
 * A gauge pressure in PSI as the bare number the preferred unit shows.
 *
 * An em dash for a value that is not a number, rather than the empty string
 * `formatQuantity` returns: these figures are readouts in running prose, and a
 * sentence with a hole in it reads worse than one with a dash.
 */
export function formatPressure(psi: number, unit: PressureUnit): string {
  const text = formatInUnit(psi, PRESSURE_UNIT_KEYS[unit]);
  return text.length === 0 ? '—' : text;
}

/** {@link formatPressure} plus its unit, e.g. `10.9 psi`. */
export function formatPressureWithUnit(psi: number, unit: PressureUnit): string {
  const text = formatPressure(psi, unit);
  return text === '—' ? text : `${text} ${pressureLabel(unit)}`;
}

/**
 * The regulator reading in the brewer's own unit, plus one cross-check.
 *
 * Always two figures, because the two audiences read different gauges and both
 * turn up in the same forum thread: a psi-preferring brewer gets bar as the
 * cross-check, everyone else gets psi — the unit every published carbonation
 * table and every set of instructions on the internet is written in.
 */
export function pressureReadout(
  psi: number,
  unit: PressureUnit,
): { readonly primary: string; readonly alternate: string } {
  return {
    primary: formatPressureWithUnit(psi, unit),
    alternate: formatPressureWithUnit(psi, unit === 'psi' ? 'bar' : 'psi'),
  };
}

/**
 * The priming rate, in the brewer's own units: `7.0 g/L`, or `0.93 oz/gal`.
 *
 * Both halves follow a preference, and they follow different ones: the sugar is
 * a mass, weighed on the same scale as the hops, and the beer is a volume. A
 * brewer who weighs in ounces and measures in gallons has never seen this
 * figure written any other way, and one who does neither has never seen it
 * written in ounces.
 */
export function formatPrimingRate(gramsPerLitre: number, units: UnitPreferences): string {
  if (!Number.isFinite(gramsPerLitre)) return '—';
  const litresPerVolume = canonicalInUnit(1, displayUnit('volume', units));
  const kgPerVolume = (gramsPerLitre / 1000) * litresPerVolume;
  const rate = formatQuantity(kgPerVolume, 'hopMass', units);
  return `${rate} ${unitLabel('hopMass', units)}/${unitLabel('volume', units)}`;
}

// ---------------------------------------------------------------------------
// Bottles
// ---------------------------------------------------------------------------

/** One bottle the per-bottle priming dose is quoted for. */
export interface BottleSize {
  /** Stable key — the React key and the test's handle on the row. */
  readonly id: string;
  /** What the bottle is called on the shelf, e.g. `500 mL`. */
  readonly label: string;
  readonly volumeL: number;
}

/**
 * The bottles a brewer actually owns, by unit system.
 *
 * Metric: the 330 mL longneck, the 500 mL Euro bottle and the 750 mL
 * cork-and-cage. US: the 12 oz longneck and the 22 oz bomber. Deliberately
 * short lists — this is a sanity check on the total ("that is 3.4 g a bottle,
 * which is one rounded teaspoon"), not a bottling worksheet.
 */
/** The cork-and-cage: the big bottle a metric brewer counts a batch in. */
export const BOTTLE_750_ML: BottleSize = { id: 'ml-750', label: '750 mL', volumeL: 0.75 };

/** Its American counterpart, the bomber. */
export const BOTTLE_22_OZ: BottleSize = {
  id: 'oz-22',
  label: '22 oz',
  volumeL: fluidOuncesToLitres(22),
};

export const METRIC_BOTTLES: readonly BottleSize[] = [
  { id: 'ml-330', label: '330 mL', volumeL: 0.33 },
  { id: 'ml-500', label: '500 mL', volumeL: 0.5 },
  BOTTLE_750_ML,
];

/** @see METRIC_BOTTLES */
export const US_BOTTLES: readonly BottleSize[] = [
  { id: 'oz-12', label: '12 oz', volumeL: fluidOuncesToLitres(12) },
  BOTTLE_22_OZ,
];

/** The bottle list that matches the workspace's volume preference. */
export function bottleSizes(units: UnitPreferences): readonly BottleSize[] {
  return units.volume === 'us' ? US_BOTTLES : METRIC_BOTTLES;
}

// ---------------------------------------------------------------------------
// Style guidance
// ---------------------------------------------------------------------------

/** A typical carbonation band for a family of styles, in volumes of CO₂. */
export interface Co2Guidance {
  readonly min: number;
  readonly max: number;
  /** The family the band belongs to, e.g. `German wheat beer`. */
  readonly family: string;
}

/**
 * Typical carbonation by BJCP category — **guidance, not a guideline**.
 *
 * The BJCP 2021 guidelines describe carbonation in words ("high", "moderately
 * low", "cask-conditioned"), never in volumes, so `Style.vitals` has no CO₂
 * range to read and adding one would be inventing a number and calling it a
 * guideline. Rather than change the catalog schema for a figure BJCP does not
 * publish, this maps the *category* to the conventional homebrewing band for
 * that family — the numbers in Palmer's *How to Brew* carbonation table and in
 * Zainasheff & Palmer's *Brewing Classic Styles*, which is where these bands
 * are actually published.
 *
 * Categories 27 and 29–34 are absent on purpose: historical beers span Gose to
 * Sahti, and 29–34 are "a base beer plus something", so their carbonation is
 * the base style's and this table has nothing honest to say about them.
 */
const CO2_BY_CATEGORY: Readonly<Record<number, Co2Guidance>> = {
  1: { min: 2.5, max: 2.8, family: 'American lager' },
  2: { min: 2.4, max: 2.8, family: 'International lager' },
  3: { min: 2.2, max: 2.6, family: 'Czech lager' },
  4: { min: 2.4, max: 2.6, family: 'Pale malty European lager' },
  5: { min: 2.4, max: 2.6, family: 'Pale bitter European beer' },
  6: { min: 2.4, max: 2.6, family: 'Amber malty European lager' },
  7: { min: 2.4, max: 2.6, family: 'Amber bitter European beer' },
  8: { min: 2.2, max: 2.6, family: 'Dark European lager' },
  9: { min: 2.2, max: 2.6, family: 'Strong European beer' },
  10: { min: 3.3, max: 4.5, family: 'German wheat beer' },
  11: { min: 1.5, max: 2.0, family: 'British bitter' },
  12: { min: 2.0, max: 2.5, family: 'Pale Commonwealth beer' },
  13: { min: 1.5, max: 2.2, family: 'Brown British beer' },
  14: { min: 1.5, max: 2.0, family: 'Scottish ale' },
  15: { min: 1.8, max: 2.4, family: 'Irish beer' },
  16: { min: 1.7, max: 2.3, family: 'Dark British beer' },
  17: { min: 1.5, max: 2.3, family: 'Strong British ale' },
  18: { min: 2.2, max: 2.7, family: 'Pale American ale' },
  19: { min: 2.2, max: 2.7, family: 'Amber and brown American beer' },
  20: { min: 2.2, max: 2.7, family: 'American porter and stout' },
  21: { min: 2.2, max: 2.7, family: 'IPA' },
  22: { min: 2.2, max: 2.7, family: 'Strong American ale' },
  23: { min: 2.4, max: 3.4, family: 'European sour ale' },
  24: { min: 2.4, max: 3.0, family: 'Belgian ale' },
  25: { min: 2.4, max: 3.4, family: 'Strong Belgian ale' },
  26: { min: 2.6, max: 3.4, family: 'Trappist ale' },
  28: { min: 2.4, max: 3.2, family: 'American wild ale' },
};

/**
 * The carbonation band typical of a style's family, or `null` when there is
 * nothing honest to say (no style chosen, a provisional style, or a specialty
 * category).
 */
export function styleCo2Guidance(style: Style | undefined): Co2Guidance | null {
  if (style === undefined) return null;
  // A provisional (X) style's `categoryNumber` is its place in the X series,
  // not a BJCP category, so reading the table with it would serve X1 the
  // American lager band. The X series has no family here; say nothing.
  if (style.categoryLetter === null) return null;
  return CO2_BY_CATEGORY[style.categoryNumber] ?? null;
}

/** Where a target sits against a band, for the "high for the style" note. */
export function co2AgainstGuidance(
  volumes: number,
  guidance: Co2Guidance | null,
): 'in-band' | 'low' | 'high' | 'unknown' {
  if (guidance === null || !Number.isFinite(volumes)) return 'unknown';
  if (volumes < guidance.min) return 'low';
  if (volumes > guidance.max) return 'high';
  return 'in-band';
}

// ---------------------------------------------------------------------------
// Reading the recipe
// ---------------------------------------------------------------------------

/**
 * The warmest temperature the fermentation schedule reaches, °C.
 *
 * This — not the current temperature, and not the primary rest — is what sets
 * the residual CO₂ the priming dose starts from on an open ferment: CO₂ driven
 * off by a warm diacetyl rest does not come back when the beer is crashed. A
 * ramp's *end* can be hotter than its start, so both ends of every step are
 * considered.
 *
 * Shared with the fermentation schedule panel, whose footer prints the same
 * figure and claims it is "the residual CO₂ the priming figure starts from" —
 * the two must not be able to disagree.
 *
 * The arithmetic is core's, reached through the document→engine bridge, for the
 * reason this module's header gives: one copy of every formula.
 *
 * @returns The peak, or `null` for a recipe with no fermentation steps.
 */
export function peakFermentationTempC(steps: readonly RecipeFermentationStep[]): number | null {
  return corePeakFermentationTempC(steps.map(toFermentationStep));
}

/**
 * What the schedule leaves in the beer, and whether pressure or temperature
 * decided it.
 *
 * The whole rule lives on core's `scheduleResidualCo2`, including the one that
 * matters here: a schedule whose **last** step is spunded is read off Henry's
 * law at that step's pressure and temperature, not off the peak. A beer crashed
 * under a spunding valve arrives at packaging holding two or three volumes,
 * which is the difference between a priming dose and no priming at all.
 *
 * @returns `null` for a recipe with no fermentation steps.
 */
export function scheduleResidual(steps: readonly RecipeFermentationStep[]): ResidualCo2 | null {
  return scheduleResidualCo2(steps.map(toFermentationStep));
}

/**
 * The default beer temperature the priming calculator starts from, °C.
 *
 * The schedule's peak when the recipe has one, otherwise 20 °C (68 °F) — a
 * room-temperature ale ferment, which is the assumption every priming
 * calculator makes when it is not told otherwise.
 */
export const DEFAULT_PACKAGING_TEMP_C = 20;

/** Keg and serving-fridge default, °C — 4 °C / ~39 °F, a beer fridge. */
export const DEFAULT_KEG_TEMP_C = 4;
